/**
 * Agreements By Period Component
 *
 * Groups and displays agreements by evaluation period with collapsible sections.
 */

import { useState } from 'react';
import type { EmployeeAgreement } from '../../lib/agreementsApi';
import { AgreementCard } from './AgreementCard';

interface AgreementsByPeriodProps {
  agreementsByEvaluation: Record<number, EmployeeAgreement[]>;
}

interface PeriodGroup {
  evaluationId: number;
  period: { start: string; end: string };
  agreements: EmployeeAgreement[];
  completed: number;
  total: number;
}

export function AgreementsByPeriod({ agreementsByEvaluation }: AgreementsByPeriodProps) {
  const [expandedPeriods, setExpandedPeriods] = useState<Set<number>>(new Set());

  // Group agreements by period
  const periodGroups: PeriodGroup[] = Object.entries(agreementsByEvaluation).map(([evalId, agreements]) => {
    const evaluationId = parseInt(evalId, 10);
    const period = agreements[0]?.evaluationPeriod;
    const completed = agreements.filter(a => a.isCompleted).length;

    return {
      evaluationId,
      period: period || { start: '', end: '' },
      agreements,
      completed,
      total: agreements.length,
    };
  }).sort((a, b) => {
    // Sort by period start date (newest first)
    return new Date(b.period.start).getTime() - new Date(a.period.start).getTime();
  });

  const togglePeriod = (evaluationId: number) => {
    const newExpanded = new Set(expandedPeriods);
    if (newExpanded.has(evaluationId)) {
      newExpanded.delete(evaluationId);
    } else {
      newExpanded.add(evaluationId);
    }
    setExpandedPeriods(newExpanded);
  };

  if (periodGroups.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <p className="mt-2 text-sm text-gray-500">Nog geen afspraken gevonden.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {periodGroups.map((group) => {
        const isExpanded = expandedPeriods.has(group.evaluationId);
        const progressPercentage = group.total > 0 ? (group.completed / group.total) * 100 : 0;

        return (
          <div key={group.evaluationId} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            {/* Period Header */}
            <button
              onClick={() => togglePeriod(group.evaluationId)}
              className="w-full px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3">
                <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium text-gray-900">
                    {new Date(group.period.start).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })} - {new Date(group.period.end).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {group.total} afspraak{group.total !== 1 ? 'en' : ''}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Progress indicator */}
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-600 min-w-[4rem] text-right">
                    {group.completed}/{group.total}
                  </span>
                </div>

                {/* Status badge */}
                {group.completed === group.total ? (
                  <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 border border-green-200 rounded-full">
                    ✓ Volledig
                  </span>
                ) : (
                  <span className="px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200 rounded-full">
                    In uitvoering
                  </span>
                )}
              </div>
            </button>

            {/* Agreements List */}
            {isExpanded && (
              <div className="p-6 space-y-3 bg-white">
                {group.agreements.map((agreement) => (
                  <AgreementCard key={agreement.id} agreement={agreement} showEvaluation={false} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
